import { useState } from 'react'
import { Plus, Pencil, Power, Trash2 } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { UtilityTypeModal } from '@/components/utilities/utility-type-modal'
import { useUtilityTypes, useToggleUtilityType, useDeleteUtilityType } from '@/hooks/use-utilities'
import { cn } from '@/lib/utils'
import type { UtilityType, UtilityTypeTier } from '@riverpark/types'

function formatKES(amount: number) {
  return `KES ${amount.toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
}

function tierSummary(
  tier: UtilityTypeTier | null,
  billingMethod: UtilityType['billingMethod'],
  unitLabel: string | null,
): string | null {
  if (!tier) return null
  return billingMethod === 'meter_based'
    ? `${formatKES(tier.baseRate!)} / ${unitLabel}`
    : formatKES(tier.flatAmount!)
}

/** One summary line for both pricing tiers — collapses to a single figure when Developer and
 * Owner are billed identically, otherwise labels each. */
function pricingSummary(ut: UtilityType): string {
  const developer = tierSummary(ut.pricing.developer, ut.billingMethod, ut.unitLabel)
  const owner = tierSummary(ut.pricing.owner, ut.billingMethod, ut.unitLabel)
  if (developer && developer === owner) return developer
  const parts: string[] = []
  if (developer) parts.push(`Developer ${developer}`)
  if (owner) parts.push(`Owner ${owner}`)
  return parts.length > 0 ? parts.join(' · ') : 'No pricing configured'
}

function MethodBadge({ method }: { method: UtilityType['billingMethod'] }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 font-sans text-[11px] font-medium',
        method === 'meter_based' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700',
      )}
    >
      {method === 'meter_based' ? 'Meter-based' : 'Flat fee'}
    </span>
  )
}

function FrequencyBadge({ frequency }: { frequency: UtilityType['frequency'] }) {
  const labels = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annual' }
  return (
    <span className="inline-flex items-center rounded border border-border-2 bg-card px-2 py-0.5 font-sans text-[11px] font-medium text-ink">
      {labels[frequency]}
    </span>
  )
}

export function AdminUtilityTypes() {
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<UtilityType | null>(null)

  const { data, isLoading } = useUtilityTypes()
  const toggle = useToggleUtilityType()
  const remove = useDeleteUtilityType()

  const types = data?.data ?? []

  function openCreate() {
    setEditing(null)
    setModalOpen(true)
  }

  function openEdit(ut: UtilityType) {
    setEditing(ut)
    setModalOpen(true)
  }

  async function handleToggle(ut: UtilityType) {
    await toggle.mutateAsync(ut.id)
  }

  async function handleDelete(ut: UtilityType) {
    if (
      !confirm(
        `Delete "${ut.name}"? This is permanent and only possible if no invoices or readings exist.`,
      )
    )
      return
    try {
      await remove.mutateAsync(ut.id)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Utility types"
        label="Billing"
        description="Configure the dues and charges billed to estate members."
        action={
          <Button onClick={openCreate}>
            <Plus size={15} strokeWidth={1.75} className="mr-1.5" />
            New utility type
          </Button>
        }
      />

      <UtilityTypeModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded border border-border-2 bg-muted/40"
            />
          ))}
        </div>
      )}

      {!isLoading && types.length === 0 && (
        <div className="rounded border border-border-2 bg-card px-6 py-12 text-center">
          <p className="font-sans text-sm text-muted-foreground">
            No utility types configured yet.
          </p>
          <Button onClick={openCreate} className="mt-4" variant="secondary">
            Create your first utility type
          </Button>
        </div>
      )}

      {!isLoading && types.length > 0 && (
        <div className="divide-y divide-border-2 overflow-hidden rounded border border-border-2">
          {types.map((ut) => (
            <div
              key={ut.id}
              className={cn(
                'flex items-start justify-between gap-4 bg-card px-5 py-4 transition-colors',
                !ut.active && 'opacity-50',
              )}
            >
              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-sans text-sm font-medium text-ink">{ut.name}</p>
                  <span className="font-mono text-xs font-medium text-muted-foreground">
                    {ut.invoicePrefix}
                  </span>
                  <MethodBadge method={ut.billingMethod} />
                  <FrequencyBadge frequency={ut.frequency} />
                  {ut.generationMode === 'auto' && (
                    <span className="inline-flex items-center rounded bg-green-50 px-2 py-0.5 font-sans text-[11px] font-medium text-green-700">
                      Auto
                    </span>
                  )}
                  {!ut.active && <Badge variant="draft">Inactive</Badge>}
                </div>

                <p className="font-sans text-sm text-muted-foreground">
                  {pricingSummary(ut)}
                  {(ut.invoiceCount > 0 || ut.readingCount > 0) && (
                    <span className="ml-3 font-sans text-xs text-muted-foreground/70">
                      {ut.invoiceCount} invoice{ut.invoiceCount !== 1 ? 's' : ''}
                      {ut.billingMethod === 'meter_based' &&
                        ` · ${ut.readingCount} reading${ut.readingCount !== 1 ? 's' : ''}`}
                    </span>
                  )}
                </p>

                {ut.description && (
                  <p className="font-sans text-xs text-muted-foreground">{ut.description}</p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(ut)} title="Edit">
                  <Pencil size={14} strokeWidth={1.75} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleToggle(ut)}
                  disabled={toggle.isPending}
                  title={ut.active ? 'Deactivate' : 'Activate'}
                >
                  <Power
                    size={14}
                    strokeWidth={1.75}
                    className={ut.active ? 'text-status-paid-text' : ''}
                  />
                </Button>
                {ut.invoiceCount === 0 && ut.readingCount === 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleDelete(ut)}
                    disabled={remove.isPending}
                    title="Delete"
                    className="text-status-overdue-text hover:bg-status-overdue-text/10"
                  >
                    <Trash2 size={14} strokeWidth={1.75} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  )
}
